/**
 * Deal Pipeline service - API calls for Phase 5 deal pipeline + feedback loop
 */
import api from './api';
import { ApiResponse } from '../types';
import {
  DealPipeline,
  PipelineListFilters,
  PipelineListResponse,
  PipelineStage,
  CreatePipelineRequest,
  UpdatePipelineRequest,
  RecordOutcomeRequest,
  ConversionFunnelData,
  InvestorReliability,
  InvestorReliabilityResponse,
  FeedbackWeights,
} from '../types/dealPipeline';

export async function fetchPipelines(
  filters?: PipelineListFilters
): Promise<ApiResponse<PipelineListResponse>> {
  try {
    const response = await api.get('/deal-pipeline', { params: filters });
    const payload = response.data;

    if (Array.isArray(payload)) {
      return {
        data: {
          pipelines: payload,
          total: payload.length,
          page: 1,
          size: payload.length,
          pages: 1,
        },
      };
    }

    return {
      data: {
        pipelines: payload?.pipelines || payload?.items || [],
        total: payload?.total ?? 0,
        page: payload?.page ?? 1,
        size: payload?.size ?? 50,
        pages: payload?.pages ?? 1,
      },
    };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to load deal pipelines' };
  }
}

export async function fetchPipeline(id: number): Promise<ApiResponse<DealPipeline>> {
  try {
    const response = await api.get(`/deal-pipeline/${id}`);
    return { data: response.data };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to load pipeline' };
  }
}

export async function createPipeline(
  req: CreatePipelineRequest
): Promise<ApiResponse<DealPipeline>> {
  try {
    const response = await api.post('/deal-pipeline', req);
    return { data: response.data };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to create pipeline' };
  }
}

export async function updatePipeline(
  id: number,
  data: UpdatePipelineRequest
): Promise<ApiResponse<DealPipeline>> {
  try {
    const response = await api.patch(`/deal-pipeline/${id}`, data);
    return { data: response.data };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to update pipeline' };
  }
}

export async function advanceStage(
  id: number,
  newStage: PipelineStage,
  notes?: string
): Promise<ApiResponse<DealPipeline>> {
  try {
    const response = await api.post(`/deal-pipeline/${id}/advance`, {
      new_stage: newStage,
      notes,
    });
    return { data: response.data };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to advance stage' };
  }
}

export async function recordOutcome(
  id: number,
  payload: RecordOutcomeRequest
): Promise<ApiResponse<DealPipeline>> {
  try {
    const response = await api.post(`/deal-pipeline/${id}/outcome`, payload);
    return { data: response.data };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to record outcome' };
  }
}

export interface ConversionAnalyticsFilters {
  investor_id?: number;
  sector?: string;
  date_from?: string;
  date_to?: string;
}

export async function getConversionAnalytics(
  filters?: ConversionAnalyticsFilters
): Promise<ApiResponse<ConversionFunnelData>> {
  try {
    const response = await api.get('/deal-pipeline/analytics/conversion', {
      params: filters,
    });
    return { data: response.data };
  } catch (error: any) {
    return {
      error: error.response?.data?.detail || 'Failed to load conversion analytics',
    };
  }
}

export async function getInvestorReliability(
  investorId?: number
): Promise<ApiResponse<InvestorReliabilityResponse>> {
  try {
    const response = await api.get('/deal-pipeline/analytics/investor-reliability', {
      params: investorId ? { investor_id: investorId } : undefined,
    });
    const payload = response.data;
    if (Array.isArray(payload)) {
      return { data: { investors: payload as InvestorReliability[] } };
    }
    if (payload?.investors) {
      return { data: payload as InvestorReliabilityResponse };
    }
    // Single-investor response shape: wrap into list
    if (payload && typeof payload === 'object' && 'investor_id' in payload) {
      return { data: { investors: [payload as InvestorReliability] } };
    }
    return { data: { investors: [] } };
  } catch (error: any) {
    return {
      error: error.response?.data?.detail || 'Failed to load investor reliability',
    };
  }
}

export async function getFeedbackWeights(): Promise<ApiResponse<FeedbackWeights>> {
  try {
    const response = await api.get('/deal-pipeline/analytics/feedback-weights');
    return { data: response.data };
  } catch (error: any) {
    return {
      error: error.response?.data?.detail || 'Failed to load feedback weights',
    };
  }
}

const dealPipelineService = {
  fetchPipelines,
  fetchPipeline,
  createPipeline,
  updatePipeline,
  advanceStage,
  recordOutcome,
  getConversionAnalytics,
  getInvestorReliability,
  getFeedbackWeights,
};

export default dealPipelineService;
